/**
 * REST client for accounts, organisation membership and invites.
 *
 * Split from `api.ts` because it is a distinct concern (identity, not board
 * content) that most of the app never touches — only the dashboard's auth
 * gate and the admin panel import this module.
 */
import { request, type Organization } from './api';

export interface PublicUser {
  id: string;
  email: string;
  name: string;
  color: string;
  isSuperAdmin: boolean;
  /** Granted by a super admin — may create organisations and own them. */
  canCreateOrgs: boolean;
  disabled: boolean;
  createdAt: number;
  lastLoginAt: number | null;
}

export interface AdminUser extends PublicUser {
  organizations: { id: string; name: string }[];
}

export interface AuthStatus {
  needsBootstrap: boolean;
  user: PublicUser | null;
  /** Canonical site origin from the server, e.g. https://ygrikboard.ru */
  publicUrl?: string;
}

export interface OrgMemberView {
  userId: string;
  orgId: string;
  role: 'admin' | 'member';
  joinedAt: number;
  name: string;
  email: string;
  color: string;
}

export interface OrgInvite {
  token: string;
  orgId: string;
  role: 'admin' | 'member';
  /** Set when the invite also grants one specific board. */
  boardId?: string | null;
  boardRole?: 'editor' | 'commenter' | 'viewer' | null;
  createdAt: number;
}

export interface InvitePreview {
  valid: boolean;
  orgId?: string;
  orgName?: string;
  role?: 'admin' | 'member';
  boardId?: string | null;
  boardName?: string | null;
  boardRole?: 'editor' | 'commenter' | 'viewer' | null;
  /** Set when accepting will also enrol the visitor as this person's student. */
  asStudentOf?: string | null;
}

const json = (body: unknown): RequestInit['body'] => JSON.stringify(body);

export const authApi = {
  // ---- account --------------------------------------------------------
  status: () => request<AuthStatus>('/auth/status'),
  me: () => request<PublicUser>('/auth/me'),
  bootstrap: (input: { email: string; password: string; name: string }) =>
    request<PublicUser>('/auth/bootstrap', { method: 'POST', body: json(input) }),
  login: (email: string, password: string) =>
    request<PublicUser>('/auth/login', { method: 'POST', body: json({ email, password }) }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),
  register: (input: { email: string; password: string; name: string; inviteToken: string }) =>
    request<{
      user: PublicUser;
      orgId: string;
      orgName: string;
      role: string;
      boardId?: string | null;
      boardName?: string | null;
    }>('/auth/register', {
      method: 'POST',
      body: json(input),
    }),

  // ---- invites ----------------------------------------------------------
  previewInvite: (token: string) => request<InvitePreview>(`/invites/${token}`),
  acceptInvite: (token: string) =>
    request<{
      orgId: string;
      orgName: string;
      role: string;
      boardId: string | null;
      boardName: string | null;
      becameStudentOf: string | null;
    }>(`/invites/${token}/accept`, { method: 'POST' }),
  listInvites: (orgId: string) => request<OrgInvite[]>(`/orgs/${orgId}/invites`),
  createInvite: (orgId: string, role: 'admin' | 'member') =>
    request<OrgInvite>(`/orgs/${orgId}/invites`, { method: 'POST', body: json({ role }) }),
  revokeInvite: (orgId: string, token: string) =>
    request<{ ok: true }>(`/orgs/${orgId}/invites/${token}`, { method: 'DELETE' }),

  // ---- members ------------------------------------------------------------
  listMembers: (orgId: string) => request<OrgMemberView[]>(`/orgs/${orgId}/members`),
  setMemberRole: (orgId: string, userId: string, role: 'admin' | 'member') =>
    request<OrgMemberView>(`/orgs/${orgId}/members/${userId}`, {
      method: 'PATCH',
      body: json({ role }),
    }),
  removeMember: (orgId: string, userId: string) =>
    request<{ ok: true }>(`/orgs/${orgId}/members/${userId}`, { method: 'DELETE' }),

  // ---- admin ------------------------------------------------------------
  adminListUsers: () => request<AdminUser[]>('/admin/users'),
  adminUpdateUser: (
    id: string,
    patch: { disabled?: boolean; isSuperAdmin?: boolean; canCreateOrgs?: boolean },
  ) => request<PublicUser>(`/admin/users/${id}`, { method: 'PATCH', body: json(patch) }),
  adminDeleteUser: (id: string) =>
    request<{ ok: true }>(`/admin/users/${id}`, { method: 'DELETE' }),
  adminListOrgs: () => request<Organization[]>('/admin/orgs'),
};

let publicOrigin = '';

/** Called after `/api/auth/status` so invite links use the configured domain. */
export function setPublicOrigin(origin: string | undefined): void {
  publicOrigin = (origin ?? '').replace(/\/$/, '');
}

/** Builds the shareable invite link for the given token. */
export function inviteUrl(token: string): string {
  const origin = publicOrigin || window.location.origin;
  const url = new URL('/', origin);
  url.searchParams.set('invite', token);
  return url.toString();
}
